import React, { useState, useRef, useEffect, useCallback } from "react";
import "./rinck-assistant.css";

const LABELS = {
  fr: {
    placeholder: "Décrivez votre projet\u2026",
    footer: "Échange confidentiel",
    sender: "L\u2019Assistant Rinck",
    summaryTitle: "Votre projet",
    catLabel: "Domaine",
    subLabel: "Type",
    descLabel: "Description",
    locLabel: "Localisation",
    surfLabel: "Surface",
    formTitle: "Pour transmettre votre dossier",
    firstName: "Prénom",
    lastName: "Nom",
    email: "E-mail",
    phone: "Téléphone",
    company: "Entreprise",
    submit: "Transmettre",
    sending: "Envoi en cours\u2026",
    done: "Votre dossier a été transmis. Nous reviendrons vers vous prochainement.",
    inputDone: "Demande transmise",
    error: "Une erreur est survenue. Veuillez réessayer.",
    contactFormTitle: "Pour transmettre votre demande",
    subjectLabel: "Sujet",
    messageLabel: "Message",
    contactDone: "Votre demande a été transmise. Nous reviendrons vers vous prochainement.",
    suggestedLinks: "Pages susceptibles de vous intéresser",
    visit: "Découvrir",
    greeting:
      "Bienvenue chez Rinck. La discrétion est au c\u0153ur de notre métier \u2014 ce que nous créons appartient à ceux qui nous le confient.\n\nJe suis là pour vous orienter. Que votre projet concerne l\u2019architecture intérieure, l\u2019agencement sur mesure ou la création de mobilier, quelques questions me permettront de vous diriger vers le bon interlocuteur.",
  },
  en: {
    placeholder: "Describe your project\u2026",
    footer: "Confidential exchange",
    sender: "The Rinck Assistant",
    summaryTitle: "Your project",
    catLabel: "Domain",
    subLabel: "Type",
    descLabel: "Description",
    locLabel: "Location",
    surfLabel: "Surface area",
    formTitle: "To submit your file",
    firstName: "First name",
    lastName: "Last name",
    email: "E-mail",
    phone: "Phone",
    company: "Company",
    submit: "Submit",
    sending: "Sending\u2026",
    done: "Your file has been submitted. We will be in touch shortly.",
    inputDone: "Inquiry submitted",
    error: "An error occurred. Please try again.",
    contactFormTitle: "To submit your inquiry",
    subjectLabel: "Subject",
    messageLabel: "Message",
    contactDone: "Your inquiry has been submitted. We will be in touch shortly.",
    suggestedLinks: "Pages you might find useful",
    visit: "Visit",
    greeting:
      "Welcome to Rinck. Discretion is at the heart of what we do \u2014 what we create belongs to those who entrust it to us.\n\nI am here to guide you. Whether your project involves interior architecture, bespoke joinery or furniture creation, a few questions will help me direct you to the right contact.",
  },
};

const CONTACT_FORM_NAMES = {
  galerie: "rinck-contact-galerie",
  presse: "rinck-contact-presse",
  decoration: "rinck-contact-decoration",
  usa: "rinck-contact-usa",
  info: "rinck-contact-info",
};

function safeParseLinks(raw) {
  try {
    const data = JSON.parse(raw.trim());
    return Array.isArray(data.items) ? data.items : null;
  } catch {
    return null;
  }
}

export const RinckAssistant = ({ currentLanguage }) => {
  const lang = currentLanguage === "en-gb" ? "en" : "fr";
  const l = LABELS[lang];

  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [apiHistory, setApiHistory] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [isDone, setIsDone] = useState(false);
  const [summaryData, setSummaryData] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [formSending, setFormSending] = useState(false);
  const [formDone, setFormDone] = useState(false);
  const [contactFormData, setContactFormData] = useState(null);
  const [contactFormSending, setContactFormSending] = useState(false);
  const [contactFormDone, setContactFormDone] = useState(false);
  const initializedRef = useRef(false);
  const prevLangRef = useRef(lang);

  const msgsRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = useCallback(() => {
    setTimeout(() => {
      // Guard inside the timeout: the panel may unmount during the 60ms delay,
      // leaving msgsRef.current null.
      if (msgsRef.current) {
        msgsRef.current.scrollTop = msgsRef.current.scrollHeight;
      }
    }, 60);
  }, []);

  const showGreeting = useCallback((targetLang) => {
    setIsTyping(true);
    setTimeout(() => {
      setIsTyping(false);
      const greeting = LABELS[targetLang].greeting;
      setMessages([{ role: "assistant", content: greeting }]);
      setApiHistory([{ role: "assistant", content: greeting }]);
    }, 900);
  }, []);

  // Initialize with greeting on first open
  useEffect(() => {
    if (isOpen && !initializedRef.current) {
      initializedRef.current = true;
      showGreeting(lang);
    }
  }, [isOpen, lang, showGreeting]);

  // Reset on language change (only if lang actually changed)
  useEffect(() => {
    if (prevLangRef.current !== lang && initializedRef.current && !isDone) {
      prevLangRef.current = lang;
      setMessages([]);
      setApiHistory([]);
      setSummaryData(null);
      setShowForm(false);
      setContactFormData(null);
      showGreeting(lang);
    }
    prevLangRef.current = lang;
  }, [lang, isDone, showGreeting]);

  useEffect(() => {
    scrollToBottom();
  }, [messages, isTyping, summaryData, showForm, contactFormData, scrollToBottom]);

  const callAPI = async (userMsg) => {
    const newHistory = [...apiHistory, { role: "user", content: userMsg }];
    setApiHistory(newHistory);
    setIsTyping(true);

    try {
      const res = await fetch("/.netlify/functions/rinck-assistant", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ messages: newHistory, lang }),
      });
      const data = await res.json();
      setIsTyping(false);

      const reply = data.content?.[0]?.text || "";

      // Parse possible tags
      const summaryMatch = reply.match(
        /\[SUMMARY_JSON\]([\s\S]*?)\[\/SUMMARY_JSON\]/
      );
      const linksMatch = reply.match(/\[LINKS\]([\s\S]*?)\[\/LINKS\]/);
      const contactMatch = reply.match(
        /\[CONTACT_FORM\]([\s\S]*?)\[\/CONTACT_FORM\]/
      );

      // Strip all tags from text content
      const cleanText = reply
        .replace(/\[SUMMARY_JSON\][\s\S]*?\[\/SUMMARY_JSON\]/, "")
        .replace(/\[LINKS\][\s\S]*?\[\/LINKS\]/, "")
        .replace(/\[CONTACT_FORM\][\s\S]*?\[\/CONTACT_FORM\]/, "")
        .trim();

      setApiHistory((prev) => [...prev, { role: "assistant", content: reply }]);

      // Build the message: use cleanText OR the intro from a tag
      let displayText = cleanText;
      if (!displayText && linksMatch) {
        try {
          displayText = JSON.parse(linksMatch[1].trim()).intro || "";
        } catch {}
      }
      if (!displayText && contactMatch) {
        try {
          displayText = JSON.parse(contactMatch[1].trim()).intro || "";
        } catch {}
      }
      if (displayText) {
        setMessages((prev) => [
          ...prev,
          { role: "assistant", content: displayText, links: linksMatch ? safeParseLinks(linksMatch[1]) : null },
        ]);
      } else if (linksMatch) {
        // No intro at all but links present — render an empty-text message just to attach links
        setMessages((prev) => [
          ...prev,
          { role: "assistant", content: "", links: safeParseLinks(linksMatch[1]) },
        ]);
      }

      // Handle SUMMARY_JSON (project qualification) — closes conversation
      if (summaryMatch) {
        try {
          const parsed = JSON.parse(summaryMatch[1].trim());
          setSummaryData(parsed);
          setTimeout(() => setShowForm(true), 600);
          setIsDone(true);
        } catch (e) {
          console.error("Failed to parse summary JSON:", e);
        }
      }

      // Handle CONTACT_FORM — closes conversation
      if (contactMatch) {
        try {
          const parsed = JSON.parse(contactMatch[1].trim());
          setContactFormData(parsed);
          setIsDone(true);
        } catch (e) {
          console.error("Failed to parse contact form JSON:", e);
        }
      }
    } catch {
      setIsTyping(false);
      setMessages((prev) => [...prev, { role: "assistant", content: l.error }]);
    }
  };

  const handleSend = () => {
    if (isDone || !inputValue.trim()) return;
    const text = inputValue.trim();
    setMessages((prev) => [...prev, { role: "user", content: text }]);
    setInputValue("");
    if (inputRef.current) {
      inputRef.current.style.height = "auto";
    }
    callAPI(text);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleResize = (e) => {
    const el = e.target;
    el.style.height = "auto";
    el.style.height = Math.min(el.scrollHeight, 110) + "px";
  };

  const handleSubmitContact = async () => {
    const emailEl = document.getElementById("ra-cf-email");
    const email = emailEl?.value?.trim();
    if (!email) {
      if (emailEl) emailEl.style.borderColor = "#c0392b";
      return;
    }

    setFormSending(true);

    const contact = {
      prenom: document.getElementById("ra-cf-prenom")?.value?.trim(),
      nom: document.getElementById("ra-cf-nom")?.value?.trim(),
      email,
      tel: document.getElementById("ra-cf-tel")?.value?.trim(),
      entreprise: document.getElementById("ra-cf-entreprise")?.value?.trim(),
    };

    try {
      const formData = new URLSearchParams();
      formData.append("form-name", "rinck-assistant-lead");
      formData.append("prenom", contact.prenom || "");
      formData.append("nom", contact.nom || "");
      formData.append("email", contact.email);
      formData.append("tel", contact.tel || "");
      formData.append("entreprise", contact.entreprise || "");
      formData.append("categorie", summaryData?.categorie || "");
      formData.append("sous_categorie", summaryData?.sous_categorie || "");
      formData.append("description", summaryData?.description || "");
      formData.append(
        "localisation",
        summaryData?.location || summaryData?.localisation || ""
      );
      formData.append("surface", summaryData?.surface || "");
      formData.append("lang", lang);

      await fetch("/", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: formData.toString(),
      });
      setFormDone(true);
    } catch (e) {
      console.error(e);
      setFormSending(false);
    }
  };

  const handleSubmitAltContact = async () => {
    const emailEl = document.getElementById("ra-acf-email");
    const email = emailEl?.value?.trim();
    if (!email) {
      if (emailEl) emailEl.style.borderColor = "#c0392b";
      return;
    }

    setContactFormSending(true);

    const target = contactFormData?.target || "info";
    const formName = CONTACT_FORM_NAMES[target] || CONTACT_FORM_NAMES.info;

    const subject =
      document.getElementById("ra-acf-subject")?.value?.trim() ||
      contactFormData?.subject ||
      "";
    const message =
      document.getElementById("ra-acf-message")?.value?.trim() ||
      contactFormData?.message ||
      "";

    const fields = {
      prenom: document.getElementById("ra-acf-prenom")?.value?.trim() || "",
      nom: document.getElementById("ra-acf-nom")?.value?.trim() || "",
      email,
      tel: document.getElementById("ra-acf-tel")?.value?.trim() || "",
      entreprise: document.getElementById("ra-acf-entreprise")?.value?.trim() || "",
      sujet: subject,
      message,
      target,
      lang,
    };

    try {
      const formData = new URLSearchParams();
      formData.append("form-name", formName);
      Object.entries(fields).forEach(([k, v]) => formData.append(k, v));

      await fetch("/", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: formData.toString(),
      });
      setContactFormDone(true);
    } catch (e) {
      console.error(e);
      setContactFormSending(false);
    }
  };

  const toggle = () => setIsOpen((prev) => !prev);

  return (
    <>
      {/* Overlay */}
      <div
        className={`ra-overlay ${isOpen ? "ra-overlay--vis" : ""}`}
        onClick={toggle}
      />

      {/* Floating trigger button */}
      <button
        className={`ra-btn ${isOpen ? "ra-btn--open" : ""}`}
        onClick={toggle}
        aria-label="L'Assistant Rinck"
      >
        <svg
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.3"
        >
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
        </svg>
      </button>

      {/* Hidden Netlify forms for bot detection at build time */}
      <form name="rinck-assistant-lead" data-netlify="true" hidden>
        <input type="hidden" name="form-name" value="rinck-assistant-lead" />
        <input name="prenom" />
        <input name="nom" />
        <input name="email" />
        <input name="tel" />
        <input name="entreprise" />
        <input name="categorie" />
        <input name="sous_categorie" />
        <input name="description" />
        <input name="localisation" />
        <input name="surface" />
        <input name="lang" />
      </form>

      {Object.values(CONTACT_FORM_NAMES).map((formName) => (
        <form key={formName} name={formName} data-netlify="true" hidden>
          <input type="hidden" name="form-name" value={formName} />
          <input name="prenom" />
          <input name="nom" />
          <input name="email" />
          <input name="tel" />
          <input name="entreprise" />
          <input name="sujet" />
          <input name="message" />
          <input name="target" />
          <input name="lang" />
        </form>
      ))}

      {/* Chat panel */}
      <div className={`ra-panel ${isOpen ? "ra-panel--vis" : ""}`}>
        {/* Header */}
        <div className="ra-header">
          <div className="ra-header__eyebrow">Rinck &middot; depuis 1841</div>
          <div className="ra-header__title">
            <em>L&rsquo;Assistant</em> Rinck
          </div>
        </div>

        {/* Messages */}
        <div className="ra-msgs" ref={msgsRef}>
          {messages.map((msg, i) => (
            <div
              key={i}
              className={`ra-msg ${
                msg.role === "assistant" ? "ra-msg--a" : "ra-msg--u"
              }`}
            >
              {msg.role === "assistant" && (
                <div className="ra-msg__sender">{l.sender}</div>
              )}
              {msg.content && (
                <div className="ra-msg__bubble">
                  {msg.content.split("\n").map((line, j) => (
                    <React.Fragment key={j}>
                      {line}
                      {j < msg.content.split("\n").length - 1 && <br />}
                    </React.Fragment>
                  ))}
                </div>
              )}
              {msg.links && msg.links.length > 0 && (
                <div className="ra-links">
                  <div className="ra-links__header">{l.suggestedLinks}</div>
                  <div className="ra-links__body">
                    {msg.links.slice(0, 3).map((link, k) => (
                      <a
                        key={k}
                        className="ra-link-item"
                        href={link.url}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <span className="ra-link-item__label">{link.label}</span>
                        <span className="ra-link-item__arrow">
                          <svg
                            width="12"
                            height="12"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="1.5"
                          >
                            <line x1="7" y1="17" x2="17" y2="7" />
                            <polyline points="7 7 17 7 17 17" />
                          </svg>
                        </span>
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}

          {/* Summary card */}
          {summaryData && (
            <div className="ra-summary">
              <div className="ra-summary__header">{l.summaryTitle}</div>
              <div className="ra-summary__body">
                <div className="ra-summary__row">
                  <div className="ra-summary__label">{l.catLabel}</div>
                  <div className="ra-summary__value">
                    <span className="ra-tag">
                      {summaryData.categorie || "\u2014"}
                    </span>
                  </div>
                </div>
                <div className="ra-summary__row">
                  <div className="ra-summary__label">{l.subLabel}</div>
                  <div className="ra-summary__value">
                    <span className="ra-tag">
                      {summaryData.sous_categorie || "\u2014"}
                    </span>
                  </div>
                </div>
                <hr className="ra-summary__divider" />
                <div className="ra-summary__row">
                  <div className="ra-summary__label">{l.descLabel}</div>
                  <div className="ra-summary__value">
                    {summaryData.description || "\u2014"}
                  </div>
                </div>
                <div className="ra-summary__row">
                  <div className="ra-summary__label">{l.locLabel}</div>
                  <div className="ra-summary__value">
                    {summaryData.location ||
                      summaryData.localisation ||
                      "\u2014"}
                  </div>
                </div>
                {summaryData.surface && (
                  <div className="ra-summary__row">
                    <div className="ra-summary__label">{l.surfLabel}</div>
                    <div className="ra-summary__value">
                      {summaryData.surface}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Contact form */}
          {showForm && !formDone && (
            <div className="ra-summary" style={{ marginTop: 4 }}>
              <div className="ra-summary__header">{l.formTitle}</div>
              <div className="ra-summary__body" style={{ gap: 10 }}>
                <div style={{ display: "flex", gap: 8 }}>
                  <div style={{ flex: 1 }}>
                    <div className="ra-summary__label" style={{ marginBottom: 4 }}>
                      {l.firstName}
                    </div>
                    <input className="ra-cf-input" id="ra-cf-prenom" type="text" />
                  </div>
                  <div style={{ flex: 1 }}>
                    <div className="ra-summary__label" style={{ marginBottom: 4 }}>
                      {l.lastName}
                    </div>
                    <input className="ra-cf-input" id="ra-cf-nom" type="text" />
                  </div>
                </div>
                <div>
                  <div className="ra-summary__label" style={{ marginBottom: 4 }}>
                    {l.email} *
                  </div>
                  <input className="ra-cf-input" id="ra-cf-email" type="email" />
                </div>
                <div>
                  <div className="ra-summary__label" style={{ marginBottom: 4 }}>
                    {l.phone}
                  </div>
                  <input className="ra-cf-input" id="ra-cf-tel" type="tel" />
                </div>
                <div>
                  <div className="ra-summary__label" style={{ marginBottom: 4 }}>
                    {l.company}
                  </div>
                  <input
                    className="ra-cf-input"
                    id="ra-cf-entreprise"
                    type="text"
                  />
                </div>
                <button
                  className="ra-cf-btn"
                  onClick={handleSubmitContact}
                  disabled={formSending}
                >
                  {formSending ? l.sending : l.submit}
                </button>
              </div>
            </div>
          )}

          {/* Form success */}
          {formDone && (
            <div className="ra-summary">
              <div className="ra-summary__header">{l.summaryTitle}</div>
              <div className="ra-summary__footer">{l.done}</div>
            </div>
          )}

          {/* Alternative contact form (non-project inquiries) */}
          {contactFormData && !contactFormDone && (
            <div className="ra-summary">
              <div className="ra-summary__header">{l.contactFormTitle}</div>
              <div className="ra-summary__body" style={{ gap: 10 }}>
                <div>
                  <div className="ra-summary__label" style={{ marginBottom: 4 }}>
                    {l.subjectLabel}
                  </div>
                  <input
                    className="ra-cf-input"
                    id="ra-acf-subject"
                    type="text"
                    defaultValue={contactFormData.subject || ""}
                  />
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                  <div style={{ flex: 1 }}>
                    <div className="ra-summary__label" style={{ marginBottom: 4 }}>
                      {l.firstName}
                    </div>
                    <input className="ra-cf-input" id="ra-acf-prenom" type="text" />
                  </div>
                  <div style={{ flex: 1 }}>
                    <div className="ra-summary__label" style={{ marginBottom: 4 }}>
                      {l.lastName}
                    </div>
                    <input className="ra-cf-input" id="ra-acf-nom" type="text" />
                  </div>
                </div>
                <div>
                  <div className="ra-summary__label" style={{ marginBottom: 4 }}>
                    {l.email} *
                  </div>
                  <input className="ra-cf-input" id="ra-acf-email" type="email" />
                </div>
                <div>
                  <div className="ra-summary__label" style={{ marginBottom: 4 }}>
                    {l.phone}
                  </div>
                  <input className="ra-cf-input" id="ra-acf-tel" type="tel" />
                </div>
                <div>
                  <div className="ra-summary__label" style={{ marginBottom: 4 }}>
                    {l.company}
                  </div>
                  <input
                    className="ra-cf-input"
                    id="ra-acf-entreprise"
                    type="text"
                  />
                </div>
                <div>
                  <div className="ra-summary__label" style={{ marginBottom: 4 }}>
                    {l.messageLabel}
                  </div>
                  <textarea
                    className="ra-cf-input ra-cf-textarea"
                    id="ra-acf-message"
                    rows="4"
                    defaultValue={contactFormData.message || ""}
                  />
                </div>
                <button
                  className="ra-cf-btn"
                  onClick={handleSubmitAltContact}
                  disabled={contactFormSending}
                >
                  {contactFormSending ? l.sending : l.submit}
                </button>
              </div>
            </div>
          )}

          {/* Alternative form success */}
          {contactFormDone && (
            <div className="ra-summary">
              <div className="ra-summary__header">{l.contactFormTitle}</div>
              <div className="ra-summary__footer">{l.contactDone}</div>
            </div>
          )}

          {/* Typing indicator */}
          {isTyping && (
            <div className="ra-typing">
              <div className="ra-dot" />
              <div className="ra-dot" />
              <div className="ra-dot" />
            </div>
          )}
        </div>

        {/* Input area */}
        <div className="ra-input-wrap">
          <div className="ra-input-row">
            <textarea
              ref={inputRef}
              className="ra-textarea"
              rows="1"
              placeholder={isDone ? l.inputDone : l.placeholder}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              onInput={handleResize}
              disabled={isDone}
            />
            <button
              className="ra-send-btn"
              onClick={handleSend}
              disabled={isDone}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.3"
              >
                <line x1="22" y1="2" x2="11" y2="13" />
                <polygon points="22 2 15 22 11 13 2 9 22 2" />
              </svg>
            </button>
          </div>
          <div className="ra-input-foot">
            <span>{l.footer}</span>
          </div>
        </div>
      </div>
    </>
  );
};
